// @flow
import React, { Component } from 'react';
import keyBy from 'lodash-es/keyBy';
import styled, { type StyledComponent } from 'styled-components';

import { DSTokenMap } from '@rover/kibble/styles';
import { ButtonType } from '@rover/react-lib/components/buttons/Button';
import LinkButton from '@rover/react-lib/components/buttons/LinkButton/LinkButton';
import RoverLoading from '@rover/react-lib/components/utils/RoverLoading';
import type { Pet } from '@rover/react-lib/types';
import { EmergencyFormConstants } from '@rover/shared/js/constants/translation';
import { isMobileEmbedded } from '@rover/utilities/userAgent';

import CareInstructions from './components/CareInstructions';
import ContactInfoContainer from './components/ContactInfo/ContactInfoContainer';
import SexInfo from './components/SexInfo';
import VetInfo from './components/VetInfo';
import type { AppProps } from '.';

type Props = {
  pets: Array<Pet>,
  getIsPetsLoading: boolean,
  savePet: (pet: Pet) => void,
  isVetComplete: boolean,
  isCareComplete: boolean,
  isContactComplete: boolean,
  isSexComplete: boolean,
  personSummary: any,
  getIsPersonSummaryLoading: boolean,
  savePersonSummaryPrivate: ({ personData: any, personUrl: string }) => void,
};

type PageProps = AppProps & Props;

type State = {
  pets: {
    [key: string]: Pet,
  },
};

const FIELD_PROPS_MAP = {
  gender: 'isSexComplete',
  veterinaryInformation: 'isVetComplete',
  careInstructions: 'isCareComplete',
};

// Heading wrapper is used around an h3 tag over an actual Heading component
// to match sizing with titles in confirmation.mustache
const HeadingWrapper: StyledComponent<{}> = styled.div`
  text-align: center;
`;

const EmergencyFormWrapper: StyledComponent<{ showfaq: boolean }> = styled.div`
  padding-top: ${DSTokenMap.SPACE_8X};
  padding-bottom: ${DSTokenMap.SPACE_8X};
  margin-bottom: ${(props) => (props.showfaq ? DSTokenMap.SPACE_8X : 0)};
  border-bottom: ${(props) =>
    props.showfaq ? `1px solid ${DSTokenMap.BORDER_COLOR_PRIMARY}` : `none`};
`;

const ButtonWrapper: StyledComponent<{}> = styled.div`
  text-align: center;
  margin-top: ${DSTokenMap.SPACE_8X};
`;

const MutedTextWrapper: StyledComponent<{}> = styled.div`
  text-align: center;
  margin-bottom: ${DSTokenMap.SPACE_5X};
`;

const MutedText: StyledComponent<{}> = styled.p`
  margin-bottom: ${DSTokenMap.SPACE_4X};
  color: ${DSTokenMap.TEXT_COLOR_SECONDARY};
`;

const EmergencyFormRoverLoading: StyledComponent<{}> = styled(RoverLoading)`
  padding-bottom: 80px;
`;

class EmergencyFormPage extends Component<PageProps, State> {
  state = {
    pets: keyBy(this.props.pets, (pet) => pet.url),
  };

  handleUpdate = (pet: Pet) => {
    this.setState((state) => ({ pets: { ...state.pets, [pet.url]: pet } }));
  };

  handleVetSave = () => {
    const pets = Object.values(this.state.pets);

    pets.forEach((pet) => {
      // $FlowFixMe flow always returns Array<mixed> from Object.values ...
      const { careInstructions, ...savedData } = pet;
      this.props.savePet(savedData);
    });
  };

  handleCareSave = () => {
    const pets = Object.values(this.state.pets);

    pets.forEach((pet) => {
      // $FlowFixMe flow always returns Array<mixed> from Object.values ...
      const { veterinaryInformation, ...savedData } = pet;
      this.props.savePet(savedData);
    });
  };

  handleSexSave = () => {
    const pets = Object.values(this.state.pets);

    pets.forEach((pet) => {
      // $FlowFixMe flow always returns Array<mixed> from Object.values ...
      const { careInstructions, veterinaryInformation, ...savedData } = pet;
      this.props.savePet(savedData);
    });
  };

  isFieldComplete = (field: string) => this.props[FIELD_PROPS_MAP[field]];

  isFieldDone = (petUrl: string, field: string) => {
    const pet = this.props.pets.find((a) => a.url === petUrl);
    if (!pet) {
      return false;
    }
    const info = pet[field];
    const edited = this.state.pets[petUrl];
    const editedInfo = edited[field];
    return this.isFieldComplete(field) && info === editedInfo;
  };

  render() {
    const {
      pets,
      getIsPetsLoading,
      isContactComplete,
      conversationUrl,
      showfaq,
      personSummary,
      getIsPersonSummaryLoading,
      savePersonSummaryPrivate,
    } = this.props;
    const petNames = pets.map((a) => a.name);
    const petConjuction = petNames.join(', ');
    // we show the sex field only when there are dogs with blanks
    const petsWithBlankSex = pets.filter((pet) => !pet.gender);

    const loadingComplete = !getIsPetsLoading && !getIsPersonSummaryLoading;

    let title = EmergencyFormConstants.missedPetsSafetyInfo;

    if (
      pets.every(
        (pet) =>
          this.isFieldDone(pet.url, 'veterinaryInformation') &&
          this.isFieldComplete('careInstructions') &&
          this.isFieldDone(pet.url, 'gender')
      ) &&
      isContactComplete
    ) {
      title = EmergencyFormConstants.petsSafetyInfo;
    }

    const renderReturnButton = () => {
      let button = '';
      if (!isMobileEmbedded()) {
        button = (
          <ButtonWrapper>
            <LinkButton href={conversationUrl} buttonType={ButtonType.PRIMARY}>
              {EmergencyFormConstants.returnButton}
            </LinkButton>
          </ButtonWrapper>
        );
      }
      return button;
    };

    return loadingComplete ? (
      <EmergencyFormWrapper showfaq={showfaq}>
        <HeadingWrapper>
          <h3>{title}</h3>
        </HeadingWrapper>
        <MutedTextWrapper>
          <MutedText>{EmergencyFormConstants.filtersSitter(petConjuction)}</MutedText>
        </MutedTextWrapper>
        {petsWithBlankSex.length ? (
          <SexInfo
            pets={Object.values(this.state.pets)}
            handleUpdate={this.handleUpdate}
            handleSave={this.handleSexSave}
            isSexComplete={this.isFieldDone}
            showNames={pets.length > 1}
          />
        ) : null}
        <VetInfo
          pets={Object.values(this.state.pets)}
          handleUpdate={this.handleUpdate}
          handleSave={this.handleVetSave}
          isVetComplete={this.isFieldDone}
          isDisabled={!!petsWithBlankSex.length}
        />
        <ContactInfoContainer
          personSummary={personSummary}
          savePersonSummaryPrivate={savePersonSummaryPrivate}
          isContactComplete={isContactComplete}
        />
        <CareInstructions pets={Object.values(this.state.pets)} isCareComplete={this.isFieldDone} />
        {renderReturnButton()}
      </EmergencyFormWrapper>
    ) : (
      <EmergencyFormRoverLoading isLoading={!loadingComplete} />
    );
  }
}

export default EmergencyFormPage;



// WEBPACK FOOTER //
// ../react-lib/src/pages/conversations/EmergencyFormPage/EmergencyFormPage.jsx